"use client";

import Link from "next/link";
import type { ReactNode, MouseEventHandler } from "react";
import styles from "./Button.module.css";

type ButtonVariant = "solid" | "outline" | "ghost" | "ruby";
type ButtonSize = "sm" | "md" | "lg";

interface BaseProps {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** UPPERCASE the label (off by default, matching the brand). */
  uppercase?: boolean;
  /** Fully rounded pill shape (default is the brand's 8px radius). */
  pill?: boolean;
  /** Leading / trailing adornments (icons). */
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  className?: string;
  disabled?: boolean;
}

interface LinkButtonProps extends BaseProps {
  /** Render as a Next.js link. */
  href: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  type?: never;
}

interface ActionButtonProps extends BaseProps {
  href?: undefined;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
}

type ButtonProps = LinkButtonProps | ActionButtonProps;

/**
 * RUBYN button. Renders a Next `<Link>` when `href` is set, otherwise a
 * native `<button>`. Visual variants map to the brand palette.
 */
export function Button(props: ButtonProps) {
  const {
    children,
    variant = "solid",
    size = "md",
    fullWidth,
    uppercase = false,
    pill = false,
    leftIcon,
    rightIcon,
    className,
    disabled,
  } = props;

  const cls = [
    styles.button,
    styles[`v-${variant}`],
    styles[`s-${size}`],
    fullWidth ? styles.full : "",
    uppercase ? styles.uppercase : "",
    pill ? styles.pill : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const inner = (
    <>
      {leftIcon && <span className={styles.icon}>{leftIcon}</span>}
      <span className={styles.label}>{children}</span>
      {rightIcon && <span className={styles.icon}>{rightIcon}</span>}
    </>
  );

  if (props.href !== undefined) {
    return (
      <Link
        href={props.href}
        className={cls}
        onClick={props.onClick}
        aria-disabled={disabled || undefined}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button
      type={props.type ?? "button"}
      className={cls}
      onClick={props.onClick}
      disabled={disabled}
    >
      {inner}
    </button>
  );
}
